import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, assetUrl, errText } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import PublishHistoryPanel from "./PublishHistoryPanel";
import EngagementPlaybook from "./EngagementPlaybook";
import SafeZoneOverlay from "./SafeZoneOverlay";
import WatermarkOverlay from "./WatermarkOverlay";
import { useBrand } from "../context/BrandContext";
import { Sparkles, Loader2, Check, XCircle, ImageIcon, Paintbrush, Film, Mic, Clock, AlertTriangle, Heart, MessageCircle, Send, Bookmark, ThumbsUp, Share2, Signal, Wifi, BatteryFull, RefreshCw, LayoutTemplate } from "lucide-react";

const PLATFORM_LABEL = { instagram: "Instagram", linkedin: "LinkedIn", twitter: "X", facebook: "Facebook", tiktok: "TikTok" };
const VOICE_LABEL = { british: "British female", warm: "Warm female", energetic: "Energetic male", adriana: "Adriana · Spanish coach", alberto: "Alberto · Spanish coach" };

const toLocalParts = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

export const PostDetailModal = ({ post, open, onClose, onUpdate, bestTime, copy, setCopy, saveCopy, genImage, genBusy, genVideo, vidBusy, onOpenEditor }) => {
  const [sched, setSched] = useState(toLocalParts(post.scheduled_at));
  const [showGuides, setShowGuides] = useState(false);
  const [title, setTitle] = useState(post.title || "");
  const [tags, setTags] = useState((post.hashtags || []).map((h) => `#${h}`).join(" "));
  const [firstComment, setFirstComment] = useState(post.first_comment || "");
  const handle = (post.brand || "harmonywellness").replace(/\s+/g, "").toLowerCase();

  // Resolve the brand's live watermark (image + saved placement) so the phone
  // preview shows exactly how the stamp lands on the rendered post.
  const { brands } = useBrand();
  const brandDoc = (brands || []).find((x) => x.name === post.brand);
  const wmUrl = brandDoc
    ? `${process.env.REACT_APP_BACKEND_URL}/api/brands/${brandDoc.id}/watermark.png?t=${brandDoc.logo_url || ""}${brandDoc.wordmark_url || ""}`
    : null;
  const wmProps = brandDoc
    ? { wmUrl, position: brandDoc.wm_position || "bottom-right", scale: brandDoc.wm_scale || 300, opacity: brandDoc.wm_opacity ?? 0.6 }
    : null;

  useEffect(() => { setSched(toLocalParts(post.scheduled_at)); }, [post.scheduled_at]);
  useEffect(() => {
    setTitle(post.title || "");
    setTags((post.hashtags || []).map((h) => `#${h}`).join(" "));
    setFirstComment(post.first_comment || "");
  }, [post.id, post.title, post.hashtags, post.first_comment]);

  const [activePanel, setActivePanel] = useState("details");
  const historyCount = (post.publish_history?.length || 0) + (post.review_status === "published" ? 1 : 0);

  // Engine Pick Learning — pull the workspace-wide success stats once when
  // the modal opens so the "Retry render" dropdown can flag the winning
  // engine, and so the Slideshow fallback CTA can quote the actual success
  // rate instead of a vague "give slideshow a shot".
  const [engineStats, setEngineStats] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/video-engine-stats");
        if (!cancelled) setEngineStats(data);
      } catch (_) { /* endpoint failure is silent — UI just skips the badge */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const recommendedEngine = engineStats?.recommended;
  const recommendedRate = recommendedEngine && engineStats?.stats?.[recommendedEngine]
    ? Math.round((engineStats.stats[recommendedEngine].success_rate || 0) * 100)
    : null;

  // Video engine fallback heuristic — 3 consecutive Veo failures on this
  // post → suggest Slideshow so admins don't burn the same failing pipeline.
  const _failStreak = (post.video_fail_history || []).slice(-3);
  const suggestSlideshowFallback = post.video_status === "failed"
    && _failStreak.length >= 3
    && _failStreak.every((f) => (f.engine || "").toLowerCase() === "veo");

  const [textBusy, setTextBusy] = useState(false);
  const regenText = async () => {
    setTextBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/regenerate-text`);
      onUpdate(data);
      setCopy(data.copy || "");
      toast.success("Text regenerated");
    } catch (e) {
      toast.error(errText(e));
    }
    setTextBusy(false);
  };

  const saveField = async (payload) => {
    try {
      const { data } = await api.patch(`/posts/${post.id}`, payload);
      onUpdate(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errText(e));
    }
  };
  const saveTags = () => {
    const arr = tags.split(/[\s,]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean);
    saveField({ hashtags: arr });
  };

  const saveSchedule = async (date, time) => {
    try {
      const iso = new Date(`${date}T${time}`).toISOString();
      const { data } = await api.patch(`/posts/${post.id}`, { scheduled_at: iso });
      onUpdate(data);
      toast.success("Schedule updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const setStatus = async (review_status) => {
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { review_status });
      onUpdate(data);
    } catch (e) { /* interceptor shows the error */ }
  };

  const changeVoice = async (voice) => {
    try {
      await api.post(`/posts/${post.id}/change-voice`, { voice });
      onUpdate({ ...post, video_status: "generating" });
      toast.info(`Re-voicing with ${VOICE_LABEL[voice]} voice — visuals stay the same (~1 min)`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const restore = async (type, url) => {
    try {
      const { data } = await api.post(`/posts/${post.id}/restore`, { type, url });
      onUpdate(data);
      toast.success(`Previous ${type} restored`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  // Mirror of ReviewFeed's Approve / Send now / Un-approve logic so the modal
  // buttons expose the same state machine (pending → approved → sent).
  const isApproved = ["approved", "scheduled", "published"].includes(post.review_status);
  const isSent = ["scheduled", "published"].includes(post.review_status);
  const [sendingNow, setSendingNow] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unapproveOpen, setUnapproveOpen] = useState(false);
  const [unapproveBusy, setUnapproveBusy] = useState(false);
  const [deleteRemote, setDeleteRemote] = useState(true);
  const [postingComment, setPostingComment] = useState(false);

  const postFirstCommentNow = async () => {
    // Persist any edits first so the endpoint uses the latest CTA text.
    const trimmed = (firstComment || "").trim();
    if (!trimmed) return toast.error("Nothing to post — write a CTA first");
    setPostingComment(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/post-first-comment`,
        { text: trimmed });
      if (data.post) onUpdate(data.post);
      toast.success(data.message || "CTA comment posted");
    } catch (e) { toast.error(errText(e)); }
    finally { setPostingComment(false); }
  };

  const handleApproveToggle = () => {
    if (!isApproved) return setStatus("approved");
    // Any un-approve — including from the plain "approved" state — surfaces
    // a confirmation dialog. The dialog itself hides the "also delete on
    // Meta" checkbox when the post hasn't been published yet.
    return setUnapproveOpen(true);
  };
  const doSendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/publish-now`);
      onUpdate({ ...post, review_status: "scheduled", scheduled_at: new Date().toISOString() });
      toast.success(data.message || "Publishing now — you'll see it live shortly");
      setConfirmOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setSendingNow(false); }
  };
  const doUnapprove = async () => {
    setUnapproveBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/unpublish`,
        { delete_remote: deleteRemote && post.review_status === "published" });
      onUpdate(data.post || { ...post, review_status: "pending_review" });
      if (data.remote_error) toast.warning(data.message || "Moved back to pending, but couldn't delete from social");
      else toast.success(data.message || "Post moved back to pending review");
      setUnapproveOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setUnapproveBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid={`post-detail-modal-${post.id}`}>
        <div className="grid md:grid-cols-[1.05fr_1fr]">
          <div className="bg-muted/50 flex flex-col items-center justify-center gap-3 p-6 md:p-8 min-h-[560px]">
            <div className="relative w-[300px] shrink-0 rounded-[2.6rem] border-[10px] border-zinc-950 bg-zinc-950 shadow-2xl overflow-hidden" data-testid={`phone-preview-${post.id}`}>
              <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-24 h-[18px] bg-zinc-950 rounded-full z-20" />
              <div className="bg-white h-[600px] overflow-y-auto rounded-[2rem]">
                <div className="flex justify-between items-center px-5 pt-2.5 pb-1 text-[10px] font-semibold text-zinc-900">
                  <span>9:41</span>
                  <span className="flex items-center gap-1"><Signal size={10} /><Wifi size={10} /><BatteryFull size={12} /></span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-pink-500 to-amber-400 p-[2px]">
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[9px] font-bold text-zinc-800">
                      {(post.brand || "HWG").split(" ").map((w) => w[0]).join("").slice(0, 3)}
                    </div>
                  </div>
                  <div className="flex-1 leading-tight">
                    <p className="text-[11px] font-semibold text-zinc-900">{handle}</p>
                    <p className="text-[9px] text-zinc-500">{PLATFORM_LABEL[post.platform] || post.platform} · Sponsored</p>
                  </div>
                  <span className="text-zinc-400 text-xs pr-1">•••</span>
                </div>
                {post.video_url ? (
                  <div className="relative">
                    <video src={assetUrl(post.video_url)} poster={post.video_poster_url ? assetUrl(post.video_poster_url) : (post.video_image_url ? assetUrl(post.video_image_url) : (post.image_url ? assetUrl(post.image_url) : undefined))} controls playsInline preload="metadata" className="w-full aspect-[9/16] object-cover bg-black" data-testid={`detail-video-${post.id}`} />
                    {wmProps && <WatermarkOverlay {...wmProps} />}
                    {showGuides && <SafeZoneOverlay wm={{ position: post.wm_position, scale: post.wm_scale }} />}
                  </div>
                ) : post.image_url ? (
                  <div className="relative">
                    <img src={assetUrl(post.image_url)} alt="" className="w-full aspect-square object-cover" />
                    {wmProps && <WatermarkOverlay {...wmProps} />}
                    {showGuides && <SafeZoneOverlay wm={{ position: post.wm_position, scale: post.wm_scale }} />}
                  </div>
                ) : (
                  <div className="aspect-square flex flex-col items-center justify-center text-zinc-400">
                    <ImageIcon size={32} className="mb-2" />
                    <p className="text-xs">No visual yet</p>
                  </div>
                )}
                <div className="flex items-center gap-3.5 px-3 pt-2.5 text-zinc-800">
                  {post.platform === "facebook" ? (
                    <><ThumbsUp size={17} /><MessageCircle size={17} /><Share2 size={17} /></>
                  ) : (
                    <><Heart size={17} /><MessageCircle size={17} /><Send size={17} /><Bookmark size={17} className="ml-auto" /></>
                  )}
                </div>
                <div className="px-3 pt-1.5 pb-4">
                  <p className="text-[10px] font-semibold text-zinc-800">128 likes</p>
                  <p className="text-[10.5px] text-zinc-800 leading-snug mt-0.5 whitespace-pre-line">
                    <span className="font-semibold">{handle}</span> {(copy || "").slice(0, 220)}{(copy || "").length > 220 ? "…" : ""}
                  </p>
                  {post.hashtags?.length > 0 && <p className="text-[10px] text-blue-700 mt-1">{post.hashtags.slice(0, 6).map((h) => `#${h}`).join(" ")}</p>}
                  {post.first_comment && (
                    <p className="text-[10px] text-zinc-600 mt-1.5"><span className="font-semibold text-zinc-800">{handle}</span> {post.first_comment.slice(0, 130)}</p>
                  )}
                </div>
              </div>
            </div>
            <button type="button" data-testid={`detail-safe-zone-toggle-${post.id}`} onClick={() => setShowGuides((v) => !v)}
              className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${showGuides ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <LayoutTemplate size={12} className="inline mr-1.5 -mt-0.5" /> {showGuides ? "Hide safe zones" : "Show safe zones"}
            </button>
            {post.video_url && post.image_url && post.video_engine_used && post.video_engine_used !== "narrate" && (
              <Button size="sm" variant="secondary" data-testid={`detail-toggle-media-${post.id}`}
                onClick={() => genVideo(post.video_voice || "british", "narrate")}>
                <ImageIcon size={13} className="mr-1" /> Static image + voiceover
              </Button>
            )}
          </div>

          <div className="p-6 space-y-4">
            {historyCount >= 2 && (
              <div className="inline-flex rounded-lg bg-muted p-1 gap-1" data-testid={`detail-panel-tabs-${post.id}`} role="tablist">
                <button type="button" data-testid={`detail-tab-details-${post.id}`}
                  role="tab" aria-selected={activePanel === "details"}
                  onClick={() => setActivePanel("details")}
                  className={`text-xs px-3 py-1.5 rounded-md transition-colors ${activePanel === "details" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
                  Details
                </button>
                <button type="button" data-testid={`detail-tab-history-${post.id}`}
                  role="tab" aria-selected={activePanel === "history"}
                  onClick={() => setActivePanel("history")}
                  className={`text-xs px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${activePanel === "history" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
                  Publish history <span className="text-[10px] rounded-full bg-primary/15 text-primary px-1.5 py-0.5">{historyCount}</span>
                </button>
              </div>
            )}
            {activePanel === "history" ? (
              <PublishHistoryPanel post={post} />
            ) : (
            <div className="space-y-4" data-testid={`detail-panel-details-${post.id}`}>
            <div className="flex flex-wrap gap-2 items-center">
              <Badge className="bg-primary text-primary-foreground border-0">{PLATFORM_LABEL[post.platform] || post.platform}</Badge>
              <Badge variant="secondary" className="capitalize">{post.pillar}</Badge>
              {post.brand && <Badge className="bg-accent text-accent-foreground border-0">{post.brand}</Badge>}
              <Badge variant="outline" className="capitalize">{post.review_status.replace("_", " ")}</Badge>
              {post.video_fallback_note && (
                <Badge className="bg-amber-500/90 text-black border-0" title={post.video_fallback_note}><AlertTriangle size={10} className="mr-1" /> Veo fallback</Badge>
              )}
              {post.video_engine === "veo" && !post.video_fallback_note && (
                <Badge className="bg-gradient-to-r from-fuchsia-500 to-amber-400 text-black border-0 font-bold tracking-wide" data-testid={`detail-motion-badge-${post.id}`}>MOTION</Badge>
              )}
            </div>
            <DialogTitle className="sr-only">{post.title}</DialogTitle>
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input data-testid={`detail-title-input-${post.id}`} value={title} onChange={(e) => setTitle(e.target.value)}
                onBlur={() => title !== (post.title || "") && saveField({ title })} className="font-heading text-base" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Caption</Label>
              <Textarea data-testid={`detail-copy-input-${post.id}`} value={copy} onChange={(e) => setCopy(e.target.value)} onBlur={saveCopy} rows={7} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Hashtags</Label>
              <Textarea data-testid={`detail-tags-input-${post.id}`} value={tags} onChange={(e) => setTags(e.target.value)}
                onBlur={saveTags} rows={2} className="text-xs text-accent" placeholder="#FitBeyond50 #Over50Fitness" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">1st comment (CTA that gets posted right after publish)</Label>
                {post.first_comment_id ? (
                  <span className="text-[10px] text-green-700 font-mono flex items-center gap-1"
                        title={`Comment ID ${post.first_comment_id}${post.first_comment_posted_at ? " · posted " + new Date(post.first_comment_posted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}`}
                        data-testid={`detail-first-comment-status-${post.id}`}>
                    <Check size={11} /> Live on {(post.platform || "").replace(/^./, c => c.toUpperCase())}
                  </span>
                ) : post.first_comment_error ? (
                  <span className="text-[10px] text-destructive font-mono" data-testid={`detail-first-comment-error-${post.id}`}
                        title={post.first_comment_error}>Meta rejected — retry below</span>
                ) : post.review_status === "published" ? (
                  <span className="text-[10px] text-amber-700 font-mono">Not posted yet</span>
                ) : (
                  <span className="text-[10px] text-muted-foreground">Preview — will post on publish</span>
                )}
              </div>
              <Textarea data-testid={`detail-first-comment-input-${post.id}`} value={firstComment} onChange={(e) => setFirstComment(e.target.value)}
                onBlur={() => firstComment !== (post.first_comment || "") && saveField({ first_comment: firstComment })} rows={2} className="text-xs" />
              {post.review_status === "published" && ["facebook", "instagram"].includes(post.platform) && !post.remote_deleted && (
                <Button size="sm" variant={post.first_comment_id ? "outline" : "default"}
                        className="text-[11px] h-7"
                        data-testid={`detail-post-first-comment-btn-${post.id}`}
                        disabled={postingComment || !firstComment.trim()}
                        onClick={postFirstCommentNow}>
                  {postingComment ? <><Loader2 size={11} className="mr-1 animate-spin" /> Posting…</> :
                   post.first_comment_id ? <><MessageCircle size={11} className="mr-1" /> Re-post CTA comment</> :
                   <><MessageCircle size={11} className="mr-1" /> Post CTA comment now</>}
                </Button>
              )}
            </div>

            {post.review_status === "published" && (
              <EngagementPlaybook post={post} onUpdate={onUpdate} />
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <Input type="date" data-testid={`detail-date-input-${post.id}`} value={sched.date}
                  onChange={(e) => { setSched({ ...sched, date: e.target.value }); saveSchedule(e.target.value, sched.time); }} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Time</Label>
                <Input type="time" data-testid={`detail-time-input-${post.id}`} value={sched.time}
                  onChange={(e) => { setSched({ ...sched, time: e.target.value }); saveSchedule(sched.date, e.target.value); }} />
              </div>
            </div>
            {bestTime?.best?.length > 0 && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock size={11} /> Best time: {bestTime.best[0].label}{bestTime.source === "benchmark" ? " · benchmark" : " · your followers"}
              </p>
            )}

            <div className="flex gap-2">
              <Button size="sm" className={`flex-1 ${isApproved ? "bg-green-600 hover:bg-green-500 text-white border-green-600" : ""}`}
                data-testid={`detail-approve-btn-${post.id}`} variant={isApproved ? "default" : "outline"}
                title={isSent ? "Tap to unapprove — you'll be asked whether to also delete from social" :
                       isApproved ? "Tap to move back to Pending review" : "Approve this post"}
                onClick={handleApproveToggle}>
                <Check size={13} className="mr-1" /> {isApproved ? "Approved · tap to undo" : "Approve"}
              </Button>
              {isApproved && (
                <Button size="sm"
                  variant={isSent ? "default" : "outline"}
                  className={`flex-1 ${isSent ? "bg-green-600 hover:bg-green-600 text-white border-green-600" : ""}`}
                  data-testid={`detail-send-now-btn-${post.id}`}
                  disabled={sendingNow || isSent}
                  title={isSent
                    ? `Sent to ${(post.platform || "").replace(/^./, c => c.toUpperCase())} · ${new Date(post.published_at || post.scheduled_at || Date.now()).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${post.permalink_url ? "  · click through in Analytics" : ""}`
                    : "Publish this approved post immediately (bypass the schedule)"}
                  onClick={() => setConfirmOpen(true)}>
                  {isSent ? <Check size={13} className="mr-1" /> :
                   sendingNow ? <Loader2 size={13} className="mr-1 animate-spin" /> :
                   <Send size={13} className="mr-1" />}
                  {isSent ? "Sent" : sendingNow ? "Sending…" : "Send now"}
                </Button>
              )}
              {post.review_status === "pending_review" && (
                <Button size="sm" variant="outline" className="flex-1 text-destructive" data-testid={`detail-changes-btn-${post.id}`} onClick={() => setStatus("changes_requested")}>
                  <XCircle size={13} className="mr-1" /> Request changes
                </Button>
              )}
            </div>

            {post.video_status === "failed" && post.video_error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 flex items-start gap-2" data-testid={`detail-video-error-${post.id}`}>
                <AlertTriangle size={14} className="text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <p className="text-[11px] font-semibold text-destructive">Last video render failed</p>
                    <p className="text-[11px] text-destructive/80 mt-0.5 break-words">{post.video_error}</p>
                  </div>
                  {suggestSlideshowFallback ? (
                    <div className="rounded-md border border-amber-500/50 bg-amber-50 px-2.5 py-2 space-y-1.5" data-testid={`detail-slideshow-fallback-${post.id}`}>
                      <p className="text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                        <AlertTriangle size={11} /> Veo has failed 3× in a row
                      </p>
                      <p className="text-[11px] text-amber-800">
                        {recommendedEngine === "narrate" && recommendedRate != null
                          ? `Slideshow is your workspace's most reliable engine (${recommendedRate}% success). Switch and stop burning Veo quota.`
                          : recommendedEngine === "veo" && recommendedRate != null
                          ? `Veo usually works here (${recommendedRate}% workspace success rate) — try again shortly, or use Slideshow to keep shipping.`
                          : `Give Slideshow a shot — it's more reliable and won't burn any more Veo quota until the pipeline recovers.`}
                      </p>
                      <Button size="sm" className="h-7 text-[11px] bg-amber-500 hover:bg-amber-600 text-black"
                        data-testid={`detail-fallback-slideshow-btn-${post.id}`}
                        disabled={vidBusy}
                        onClick={() => genVideo(post.video_voice || "british", "narrate")}>
                        {vidBusy ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Film size={11} className="mr-1" />}
                        Retry with Slideshow
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-muted-foreground">
                      {recommendedEngine && recommendedRate != null
                        ? <>Recommend engine: <b className="capitalize">{recommendedEngine === "narrate" ? "Slideshow" : "Full motion"}</b> · {recommendedRate}% success across this workspace. Pick it from the retry menu below.</>
                        : <>Tap <b>Retry render</b> below to try again — pick a different engine if the same one keeps failing.</>}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Button className="h-10 bg-primary text-primary-foreground hover:bg-primary/85" data-testid={`detail-regen-text-btn-${post.id}`} disabled={textBusy} onClick={regenText}>
                {textBusy ? <Loader2 size={15} className="animate-spin mr-1" /> : <RefreshCw size={15} className="mr-1" />} Regenerate text
              </Button>
              <Button className="h-10 bg-primary text-primary-foreground hover:bg-primary/85" data-testid={`detail-gen-image-btn-${post.id}`} disabled={genBusy} onClick={genImage}>
                {genBusy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Sparkles size={15} className="mr-1" />} {post.image_url ? "Regenerate visual" : "AI visual"}
              </Button>
              <Button className="h-10 bg-primary text-primary-foreground hover:bg-primary/85" data-testid={`detail-edit-visual-btn-${post.id}`} disabled={!post.image_url} onClick={onOpenEditor}>
                <Paintbrush size={15} className="mr-1" /> Edit visual
              </Button>
              <div className="flex items-center gap-1.5 border border-border rounded-md p-1 bg-muted/40" data-testid={`detail-video-engine-toggle-${post.id}`} title="Default video style when this post gets narrated">
                <span className="text-[11px] text-muted-foreground px-1.5">Style:</span>
                {[{ v: "narrate", l: "Slideshow" }, { v: "veo", l: "Full motion" }].map(({ v, l }) => (
                  <button key={v} type="button"
                    data-testid={`detail-video-engine-${v}-${post.id}`}
                    onClick={() => saveField({ video_engine: v })}
                    className={`text-[11px] px-2 py-1 rounded-md transition-colors ${((post.video_engine || "narrate") === v) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className={`h-10 ${post.video_status === "failed"
                        ? "bg-destructive text-destructive-foreground hover:bg-destructive/85"
                        : "bg-primary text-primary-foreground hover:bg-primary/85"}`}
                    data-testid={`detail-gen-video-btn-${post.id}`} disabled={vidBusy}
                    title={post.video_status === "failed" && post.video_error
                      ? `Last render failed: ${post.video_error} — tap to retry`
                      : post.video_url ? "Re-render the video with the same or a different engine"
                      : "Kick off a video render — voice + engine picker follows"}>
                    {vidBusy ? <Loader2 size={15} className="animate-spin mr-1" />
                     : post.video_status === "failed" ? <RefreshCw size={15} className="mr-1" />
                     : <Film size={15} className="mr-1" />}
                    {post.video_status === "failed" ? "Retry render"
                     : post.video_url ? "Regenerate video"
                     : "Generate video"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-xs">Pick a video style — voice stays on {VOICE_LABEL[post.video_voice] || "British female"}. Change it below.</DropdownMenuLabel>
                  <DropdownMenuItem data-testid={`detail-video-slideshow-${post.id}`} onClick={() => genVideo(post.video_voice || "british", "narrate")}>
                    Slideshow · free · uses this post's image + narrated body copy
                    {recommendedEngine === "narrate" && recommendedRate != null && (
                      <span className="ml-2 text-[9px] rounded-full bg-emerald-500/15 text-emerald-700 px-1.5 py-0.5 font-semibold" data-testid={`detail-recommended-narrate-${post.id}`}>★ Recommended · {recommendedRate}%</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`detail-video-fullmotion-${post.id}`} onClick={() => genVideo(post.video_voice || "british", "veo")}>
                    Full motion · Veo (~$1/scene) · animated scenes generated from the body copy
                    {recommendedEngine === "veo" && recommendedRate != null && (
                      <span className="ml-2 text-[9px] rounded-full bg-emerald-500/15 text-emerald-700 px-1.5 py-0.5 font-semibold" data-testid={`detail-recommended-veo-${post.id}`}>★ Recommended · {recommendedRate}%</span>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-10 bg-primary text-primary-foreground hover:bg-primary/85" data-testid={`detail-voice-btn-${post.id}`} disabled={!post.video_url || vidBusy}>
                    {vidBusy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Mic size={15} className="mr-1" />} Change voice
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-xs">Voice only — visuals stay the same</DropdownMenuLabel>
                  {Object.entries(VOICE_LABEL).map(([v, l]) => (
                    <DropdownMenuItem key={v} data-testid={`detail-voice-${v}-${post.id}`} disabled={post.video_voice === v} onClick={() => changeVoice(v)}>
                      {l}{post.video_voice === v ? " · current" : ""}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {(post.image_history?.length > 0 || post.video_history?.length > 0) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Previous versions — tap to restore</Label>
                <div className="flex gap-2 flex-wrap">
                  {(post.image_history || []).slice().reverse().map((h, i) => (
                    <button key={h.url} type="button" className="relative w-14 h-14 rounded overflow-hidden border border-border hover:ring-2 ring-primary transition-shadow"
                      title="Restore this image" data-testid={`restore-image-${i}-${post.id}`} onClick={() => restore("image", h.url)}>
                      <img src={assetUrl(h.url)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {(post.video_history || []).slice().reverse().map((h, i) => (
                    <button key={h.url} type="button" className="w-14 h-14 rounded border border-border bg-muted flex flex-col items-center justify-center gap-0.5 text-[9px] text-muted-foreground hover:ring-2 ring-primary transition-shadow"
                      title="Restore this video" data-testid={`restore-video-${i}-${post.id}`} onClick={() => restore("video", h.url)}>
                      <Film size={14} /> video
                    </button>
                  ))}
                </div>
              </div>
            )}
            {post.video_voice && <p className="text-[11px] text-muted-foreground">Current voiceover: {VOICE_LABEL[post.video_voice] || post.video_voice}</p>}
            </div>
            )}
          </div>
        </div>
      </DialogContent>

      <Dialog open={confirmOpen} onOpenChange={(o) => !sendingNow && setConfirmOpen(o)}>
        <DialogContent className="sm:max-w-md !bg-[#fbf0eb] !text-[#1d3d47] border-2 border-[#c69d56] shadow-2xl" data-testid={`detail-send-now-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Send size={18} /> Publish now?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 !text-[#1d3d47]/85">
              <span className="block text-foreground font-medium">"{post.title}"</span>
              <span className="block text-xs !text-[#1d3d47]/75">
                Going live on <b className="capitalize">{post.platform}</b> within a minute.
              </span>
              {post.scheduled_at && (
                <span className="block text-xs !text-[#1d3d47]/75">
                  Overrides the currently scheduled time
                  {" "}({new Date(post.scheduled_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={sendingNow}
                    data-testid={`detail-send-now-cancel-${post.id}`}>Cancel</Button>
            <Button onClick={doSendNow} disabled={sendingNow}
                    data-testid={`detail-send-now-confirm-${post.id}`}>
              {sendingNow ? <><Loader2 size={13} className="mr-1 animate-spin" /> Sending…</> : <><Send size={13} className="mr-1" /> Send now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unapproveOpen} onOpenChange={(o) => !unapproveBusy && setUnapproveOpen(o)}>
        <DialogContent className="sm:max-w-md !bg-[#fbf0eb] !text-[#1d3d47] border-2 border-[#c69d56] shadow-2xl" data-testid={`detail-unapprove-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <XCircle size={18} /> Un-approve this post?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 !text-[#1d3d47]/85">
              <span className="block text-foreground font-medium">"{post.title}"</span>
              <span className="block text-xs !text-[#1d3d47]/75">
                {post.review_status === "published"
                  ? <>This post is already live on <b className="capitalize">{post.platform}</b>. Moving it back to Pending review keeps the local record editable.</>
                  : post.review_status === "scheduled"
                    ? <>This post is queued for <b className="capitalize">{post.platform}</b>. Un-approving cancels the queued publish and moves it back to Pending review.</>
                    : <>This post is approved and waiting for its scheduled window. Un-approving moves it back to Pending review so you can edit it further.</>}
              </span>
            </DialogDescription>
          </DialogHeader>
          {post.review_status === "published" && (
            <label className="flex items-start gap-2 text-xs cursor-pointer rounded-md border p-3 bg-muted/40"
                   data-testid={`detail-unapprove-delete-remote-${post.id}`}>
              <input type="checkbox" checked={deleteRemote}
                     onChange={(e) => setDeleteRemote(e.target.checked)}
                     className="mt-0.5" />
              <span>
                <b>Also delete the post from {post.platform}</b>
                <span className="block !text-[#1d3d47]/70 mt-0.5">
                  Removes it from your brand's feed via the Meta Graph API. Uncheck to keep it live even though it's unapproved here.
                </span>
              </span>
            </label>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setUnapproveOpen(false)} disabled={unapproveBusy}
                    data-testid={`detail-unapprove-cancel-${post.id}`}>Cancel</Button>
            <Button variant="destructive" onClick={doUnapprove} disabled={unapproveBusy}
                    data-testid={`detail-unapprove-confirm-${post.id}`}>
              {unapproveBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Working…</> :
               <><XCircle size={13} className="mr-1" /> {post.review_status === "published" && deleteRemote ? "Un-approve & delete" : "Un-approve"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
